<template lang="pug">
.result-page
  Loading(
    v-model:active="recommendationsPageLoading"
      :is-full-page="true"
      :opacity="1"
      :lock-scroll="true"
  )
  .header-wrapper
    ResultNav
    ScheduleBar
  .container
      .main
        .tour-wrapper(v-if="tourButtonPos === ButtonPosition.MainPanel")
          TourButton
        .page-content(ref="content")
          Message(v-if="!!houseDescription" class="typology-message") {{ $t('typologyMatch', { description: houseDescription }) }}
          router-view(v-slot="{Component}")
            transition(name="fade")
              component(:is="Component")
        ResultAsidePanel(v-if="currentRoute !== 'costs-and-savings'")
  ConfirmDialog
  Dialog(v-model:visible="showGrantDialog" :header="$t('recommendations.grantMsgTitle')" style="max-width: 450px;" :modal="true" :dismissableMask="true")
    ul.grant-list
      li(v-for="msg in grantMessages" :key="msg.details")
        span.grant-span(v-if="getGrantTitle(msg.recommendationId)") {{ getGrantTitle(msg.recommendationId) }}: 
        | {{ msg.details }}

</template>
<script lang="ts">
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import ResultNav from "@/components/result/ResultNav.vue";
import ScheduleBar from "@/components/result/ScheduleBar.vue";
import { storeToRefs } from "pinia";
import { useRecommendations } from "@/store/recommendations";
import Loading from "vue-loading-overlay";
import ResultAsidePanel from "@/components/result/aside/ResultAsidePanel.vue";
import { useScroll } from "@vueuse/core";
import { LayoutEvent, on } from "@/helpers";
import { useSettings } from "@/store";
import ConfirmDialog from "primevue/confirmdialog";
import Dialog from "primevue/dialog";
import { useRouter } from "vue-router";
import Message from "primevue/message";
import { ButtonPosition, Recommendation, RecommendationGroup } from "@/types";
import TourButton from "@/components/result/TourButton.vue";

export default defineComponent({
  name: "ResultPage",
  components: {
    ResultNav,
    ScheduleBar,
    Loading,
    ResultAsidePanel,
    ConfirmDialog,
    Dialog,
    Message,
    TourButton,
  },
  setup() {
    const { recommendationsPageLoading, isLoadingReport, report } = storeToRefs(
      useRecommendations()
    );
    const router = useRouter();
    const currentRoute = computed(() => router.currentRoute.value.name);
    if (currentRoute.value === "selections-page")
      router.push("/recommendations");

    const showGrantDialog = ref(false);
    const showMortgageDialog = ref(false);

    const { y } = useScroll(window, { behavior: "smooth" });

    const settingsStore = useSettings();
    const { initializeTour } = settingsStore;
    const { tour, settings } = storeToRefs(settingsStore);

    const { recommendationGroups, grantMessages } = storeToRefs(
      useRecommendations()
    );

    const houseDescription =
      report.value?.typologyMatch?.typologyDetails.description;

    const tourButtonPos =
      settings.value?.recommendationsPageOptions?.tourButtonPosition;

    onMounted(() => {
      y.value = 0;
      if (!settings.value?.tourOptions?.disableTour) {
        if (recommendationsPageLoading.value) {
          watch(recommendationsPageLoading, () => startTour());
        } else {
          startTour();
        }
      }
    });
    on(LayoutEvent.ShowGrantDialog, () => {
      showGrantDialog.value = true;
    });

    function getGrantTitle(recomId: string) {
      if (!recomId) return;
      return recommendationGroups.value
        .flatMap((group: RecommendationGroup) => group.recommendations)
        .find((r: Recommendation) => r.id === recomId)?.name;
    }

    function startTour() {
      setTimeout(() => {
        initializeTour();
        if (settingsStore.autoStartTour && tour.value) {
          tour.value.start();
          settingsStore.setAutoStartTour(false);
        }
      }, 500);
    }

    const content = ref<HTMLDivElement>();
    on(LayoutEvent.ScrollToContent, () => {
      const mobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      if (content.value && mobile) {
        y.value = content.value.getBoundingClientRect().y;
      }
    });

    return {
      isLoadingReport,
      recommendationsPageLoading,
      content,
      getGrantTitle,
      grantMessages,
      showGrantDialog,
      currentRoute,
      showMortgageDialog,
      houseDescription,
      ButtonPosition,
      tourButtonPos,
    };
  },
});
</script>

<style lang="scss">
.result-page {
  height: 100%;
  .header-wrapper {
    width: 100%;
  }
  .container {
    height: 100%;

    .p-scrollpanel {
      height: 100%;
    }

    .main {
      display: flex;
      padding: 1rem 3rem;
      gap: 1.5rem;
      .page-content {
        flex: 1;
        min-width: 0;
        width: 100%;
      }
    }
  }
}

.fade-enter-active {
  transition: opacity 500ms ease;
}

.fade-enter-from {
  opacity: 0;
}
@media screen and (max-width: 1200px) {
  .result-page {
    .container {
      .main {
        padding: 1rem;
      }
    }
  }
}
@media screen and (max-width: 1120px) {
  .result-page {
    .container {
      .main {
        flex-direction: column-reverse;
      }
    }
  }
}
.grant-list {
  padding-left: 1.25rem;
  li {
    margin-bottom: 0.75rem;
  }
  .grant-span {
    display: inline;
    font-weight: bold;
  }
}

.tour-wrapper {
  z-index: 9;
  padding-bottom: 0.5rem;
}
</style>
